/**
 * ControlStateMachine
 * 
 * Manages battle control flow state
 * Determines what actions are allowed based on current state
 */

import { BattleStatus, ControlState } from "@/core/types/battle.types";



/**
 * State machine for battle control flow
 */
export class ControlStateMachine {
    private state: ControlState = ControlState.Initializing;
    private previousState: ControlState | null = null;
    
    // State history for debugging
    private stateHistory: Array<{
        from: ControlState;
        to: ControlState;
        timestamp: number;
    }> = [];
    
    private readonly MAX_HISTORY = 20;
    
    // Callbacks for state transitions
    private onStateChangeCallbacks: Array<(from: ControlState, to: ControlState) => void> = [];
    
    constructor(private enableLogging: boolean = true) {}
    
    // ═══════════════════════════════════════════════════════════════════════
    // STATE TRANSITIONS
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Transition to new state
     */
    setState(newState: ControlState): void {
        if (this.state === newState) return;
        
        // Validate transition
        if (!this.isValidTransition(this.state, newState)) {
            console.warn(
                `[ControlStateMachine] Invalid transition: ${this.state} → ${newState}`
            );
            return;
        }
        
        const oldState = this.state;
        this.previousState = oldState;
        this.state = newState;
        
        // Record history
        this.stateHistory.push({
            from: oldState,
            to: newState,
            timestamp: Date.now()
        });
        
        // Trim history
        if (this.stateHistory.length > this.MAX_HISTORY) {
            this.stateHistory.shift();
        }
        
        // Log transition
        if (this.enableLogging) {
            console.log(`[ControlState] ${oldState} → ${newState}`);
        }
        
        // Notify callbacks
        this.onStateChangeCallbacks.forEach(callback => {
            callback(oldState, newState);
        });
    }
    
    /**
     * Validate state transition
     */
    private isValidTransition(from: ControlState, to: ControlState): boolean {
        // Define valid transitions
        const validTransitions: Record<ControlState, ControlState[]> = {
            [ControlState.Initializing]: [
                ControlState.Idle,
                ControlState.EnemyTurn,
                ControlState.Ended
            ],
            
            [ControlState.Idle]: [
                ControlState.PlayerMoving,
                ControlState.PlayerAttacking,
                ControlState.SkillTargeting,
                ControlState.EnemyTurn,
                ControlState.Ended
            ],
            
            [ControlState.SkillTargeting]: [
                ControlState.Idle,
                ControlState.PlayerAttacking,
                ControlState.EnemyTurn,
                ControlState.Ended
            ],
            
            [ControlState.PlayerMoving]: [
                ControlState.Idle,
                ControlState.EnemyTurn,
                ControlState.Ended
            ],
            
            [ControlState.PlayerAttacking]: [
                ControlState.Idle,
                ControlState.EnemyTurn,
                ControlState.Ended
            ],
            
            [ControlState.EnemyTurn]: [
                ControlState.Idle,
                ControlState.Ended
            ],
            
            [ControlState.Ended]: [] // No transitions from ended state
        };
        
        return validTransitions[from]?.includes(to) ?? false;
    }
    
    /**
     * Return to previous state (useful for canceling actions)
     */
    revertToPreviousState(): void {
        if (this.previousState) {
            this.setState(this.previousState);
        }
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // STATE QUERIES
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Get current state
     */
    getState(): ControlState {
        return this.state;
    }
    
    /**
     * Check if in specific state
     */
    is(state: ControlState): boolean {
        return this.state === state;
    }
    
    /**
     * Check if in any of the given states
     */
    isAnyOf(...states: ControlState[]): boolean {
        return states.includes(this.state);
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // INPUT VALIDATION
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Check if user input should be processed
     */
    allowsInput(): boolean {
        return this.state === ControlState.Idle || 
               this.state === ControlState.SkillTargeting;
    }
    
    /**
     * Check if hover interactions are allowed
     */
    allowsHover(): boolean {
        return this.allowsInput();
    }
    
    /**
     * Check if click interactions are allowed
     */
    allowsClick(): boolean {
        return this.allowsInput();
    }
    
    /**
     * Check if movement is allowed
     */
    allowsMovement(): boolean {
        return this.state === ControlState.Idle;
    }
    
    /**
     * Check if attack is allowed
     */
    allowsAttack(): boolean {
        return this.state === ControlState.Idle || 
               this.state === ControlState.SkillTargeting;
    }
    
    /**
     * Check if skill selection is allowed
     */
    allowsSkillSelection(): boolean {
        return this.state === ControlState.Idle ||
               this.state === ControlState.SkillTargeting;
    }
    
    /**
     * Check if in blocking state (animations, enemy turn)
     */
    isBlocking(): boolean {
        return this.state === ControlState.PlayerMoving ||
               this.state === ControlState.PlayerAttacking ||
               this.state === ControlState.EnemyTurn;
    }
    
    /**
     * Check if battle is active (not ended or initializing)
     */
    isBattleActive(): boolean {
        return this.state !== ControlState.Initializing &&
               this.state !== ControlState.Ended;
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // GAME STATE INTEGRATION
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Check if input should be handled
     * Combines state machine check with game status
     */
    canHandleInput(battleStatus: BattleStatus): boolean {
        // Battle must be playing
        if (battleStatus !== BattleStatus.Playing) {
            return false;
        }
        
        // State machine must allow input
        return this.allowsInput();
    }
    
    /**
     * Sync state machine with game status
     * Call this when battle status changes
     */
    syncWithBattleStatus(status: BattleStatus): void {
        switch (status) {
            case BattleStatus.Preparing:
                this.setState(ControlState.Initializing);
                break;
                
            case BattleStatus.Playing:
                if (this.is(ControlState.Initializing)) {
                    this.setState(ControlState.Idle);
                }
                break;
                
            case BattleStatus.Victory:
            case BattleStatus.Defeat:
                this.setState(ControlState.Ended);
                break;
        }
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // CALLBACKS & OBSERVERS
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Register callback for state changes
     */
    onStateChange(callback: (from: ControlState, to: ControlState) => void): () => void {
        this.onStateChangeCallbacks.push(callback);
        
        // Return unsubscribe function
        return () => {
            const index = this.onStateChangeCallbacks.indexOf(callback);
            if (index !== -1) {
                this.onStateChangeCallbacks.splice(index, 1);
            }
        };
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // DEBUGGING & DIAGNOSTICS
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Get state history for debugging
     */
    getHistory(): Array<{ from: ControlState; to: ControlState; timestamp: number }> {
        return [...this.stateHistory];
    }
    
    /**
     * Get formatted history string
     */
    getHistoryString(): string {
        return this.stateHistory
            .map(({ from, to, timestamp }) => {
                const time = new Date(timestamp).toLocaleTimeString();
                return `[${time}] ${from} → ${to}`;
            })
            .join('\n');
    }
    
    /**
     * Print current state info
     */
    debugPrint(): void {
        console.log('=== ControlStateMachine State ===');
        console.log('Current:', this.state);
        console.log('Previous:', this.previousState);
        console.log('Allows Input:', this.allowsInput());
        console.log('Is Blocking:', this.isBlocking());
        console.log('\nRecent History:');
        console.log(this.getHistoryString());
    }
    
    /**
     * Reset state machine
     */
    reset(): void {
        this.state = ControlState.Initializing;
        this.previousState = null;
        this.stateHistory = [];
        
        if (this.enableLogging) {
            console.log('[ControlStateMachine] Reset');
        }
    }
}